import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Zap,
  LayoutDashboard,
  MapPin,
  Star,
  ArrowRight,
  CheckCircle,
  Clock,
  CreditCard,
  Smartphone,
  GraduationCap,
  Handshake,
  ChevronDown,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { useState, useEffect, useRef, useMemo } from "react";
import { getMonthlyStats } from "@/lib/statsGenerator";

const reviews = [
  "They found me a test within 24 hours of signing up. Incredible speed!",
  "I was facing a 5-month wait, but got a slot in just ten days.",
  "Failed on Monday, had a new booking by Wednesday. Brilliant!",
  "I was skeptical at first, but they completely won me over. Fast and legit.",
  "The dashboard updates were so helpful. Always knew what was happening.",
  "Worth every penny. Took away all the stress of searching myself.",
  "My instructor recommended them and I'm so glad I listened.",
  "Got my daughter a test at our local centre when we thought it was impossible.",
  "The signup form was simple and they handled everything from there.",
  "Took away all the hassle of refreshing the DVSA site constantly.",
  "I chose the priority search and got a date 3 months earlier.",
  "Customer service was polite and incredibly helpful.",
  "I needed my licence for work urgently and they truly delivered.",
  "Stress-free experience. Just gave them my dates and they sorted it.",
  "Found a cancellation in my exact preferred time window.",
  "Much faster than any app I tried before. Highly recommend.",
  "Rebooked my test at a much closer centre. Lifesaver!",
  "They replied instantly, even on a Sunday. Great support team.",
  "Got a notification the very next day. Amazingly quick.",
  "My son's test was moved forward by 4 months. We're thrilled!",
];

const reviews2 = [
  "Professional setup and everything explained clearly from day one.",
  "Had a university deadline and they matched it perfectly.",
  "Used them for both my kids — great results every time.",
  "They listen to your actual preferences, not just random slots.",
  "No hidden fees, no fuss. Straightforward excellent service.",
  "Found a cancellation at a notoriously difficult test centre.",
  "The confirmation came through so quickly I couldn't believe it.",
  "Best cancellation service out there. Don't bother with the rest.",
  "Seamless from start to finish. Highly recommended to all learners.",
  "They genuinely care about getting you the best date possible.",
  "My original booking was miles away — they got me one locally.",
  "Great value considering the hours of refreshing it saved me.",
  "Fast, efficient, and super friendly. What more could you want?",
  "Their system was incredibly user-friendly even for a technophobe.",
  "Found me a weekend test which was exactly what I needed.",
  "Sorted me out in under 48 hours when I was getting desperate.",
  "I failed and was back in the seat by Friday. Rapid turnaround.",
  "Handed the search over to professionals and they delivered.",
  "Found a date before my theory certificate expired. Lifesavers!",
  "The priority option is absolutely worth it if you're in a rush.",
];

const faqs = [
  {
    q: "How does it actually work?",
    a: "You sign up, tell us where and when you want your test, and we do the rest. Our system watches for DVSA cancellations 24/7 and grabs the best slot the second it appears.",
  },
  {
    q: "How quick is it?",
    a: "It depends on cancellations in your area — could be hours, could be a couple of weeks. But we never stop looking and most people get sorted way faster than expected.",
  },
  {
    q: "Do I need a booking already?",
    a: "For Standard (£48), yes — you need a DVSA test reference. If you don't have one yet, our Full Support (£148) includes booking your first test for you.",
  },
  {
    q: "Can I get my money back?",
    a: "Please check our terms for details on our refund policy. We aim to be fair and transparent.",
  },
  {
    q: "Why can't I just find one myself?",
    a: "Cancellations get snapped up in milliseconds by bots and automated systems. By the time you load the DVSA page, it's already gone. We give you that speed advantage.",
  },
  {
    q: "What areas do you cover?",
    a: "Every DVSA test centre in England, Scotland, and Wales. Pick multiple centres and we'll search all of them at the same time.",
  },
];

function AnimatedCounter({ target, suffix = "" }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 2000;
          const steps = 60;
          const increment = target / steps;
          let current = 0;
          const timer = setInterval(() => {
            current += increment;
            if (current >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.3 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <div ref={ref} className="text-4xl md:text-5xl font-black text-primary">
      {count.toLocaleString()}
      {suffix}
    </div>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-muted/50 transition-colors"
      >
        <span className="font-semibold text-foreground pr-4">{q}</span>
        <ChevronDown
          className={`h-5 w-5 text-muted-foreground flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="px-5 pb-5 text-muted-foreground text-sm leading-relaxed">{a}</div>}
    </div>
  );
}

function ReviewRow({ items, direction }: { items: string[]; direction: "left" | "right" }) {
  const doubled = [...items, ...items];
  return (
    <div className="overflow-hidden">
      <div
        className={`flex gap-4 ${direction === "left" ? "animate-scroll-left" : "animate-scroll-right"}`}
        style={{ width: "max-content" }}
      >
        {doubled.map((text, i) => (
          <div key={i} className="flex-shrink-0 w-80 bg-card border border-border rounded-xl p-5">
            <div className="flex gap-0.5 mb-2">
              {[...Array(5)].map((_, j) => (
                <Star key={j} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-sm text-card-foreground leading-relaxed">{text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const Index = () => {
  const monthlyStats = useMemo(() => getMonthlyStats(), []);
  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero — punchy, direct, teen-friendly */}
      <section className="hero-gradient py-16 md:py-24 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: "radial-gradient(circle at 30% 50%, hsl(var(--primary-glow)) 0%, transparent 50%)",
          }}
        />
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 rounded-full px-4 py-1.5 mb-6">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <span className="text-sm text-primary-foreground/90">3,000+ 5-Star Reviews</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-black text-primary-foreground leading-tight mb-4 tracking-tight">
            Stop Waiting Months
            <br />
            <span className="text-primary-foreground">Get Your Test ASAP</span>
          </h1>
          <p className="text-lg md:text-xl text-primary-foreground/70 max-w-xl mx-auto mb-8">
            We find driving test cancellations <strong className="text-primary-foreground">24/7</strong> and book them
            for you instantly. 2 min setup. Track everything from your dashboard.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
            <Button
              asChild
              size="lg"
              className="text-lg px-10 py-7 font-bold rounded-full w-full sm:w-auto"
              style={{ background: "#16a34a", color: "white" }}
            >
              <Link to="/signup">
                Get Started <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              className="text-lg px-8 py-7 font-semibold rounded-full border border-primary-foreground/30 text-primary-foreground bg-transparent hover:bg-primary-foreground/10 w-full sm:w-auto"
            >
              <a href="#how-it-works">How does it work?</a>
            </Button>
          </div>

          <p className="text-sm text-primary-foreground/50">No subscription. No hidden fees. Cancel anytime.</p>

          {/* Feature pills */}
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            {[
              { icon: Zap, text: "Results in days, not months" },
              { icon: LayoutDashboard, text: "Live dashboard updates" },
              { icon: MapPin, text: "Every UK test centre" },
            ].map((f) => (
              <div
                key={f.text}
                className="flex items-center gap-2 bg-foreground/10 backdrop-blur-sm border border-primary-foreground/10 rounded-full px-4 py-2"
              >
                <f.icon className="h-4 w-4 text-accent" />
                <span className="text-sm text-primary-foreground font-medium">{f.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-background border-b border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto text-center">
            <div>
              <AnimatedCounter target={12500} suffix="+" />
              <p className="text-sm text-muted-foreground mt-1">Tests Found</p>
            </div>
            <div>
              <AnimatedCounter target={monthlyStats.thisMonth} />
              <p className="text-sm text-muted-foreground mt-1">Tests Found This Month</p>
            </div>
            <div>
              <AnimatedCounter target={monthlyStats.yesterday} />
              <p className="text-sm text-muted-foreground mt-1">Yesterday</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works — simplified, 3 dead-simple steps */}
      <section id="how-it-works" className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-2 text-foreground">Dead Simple. 3 Steps.</h2>
          <p className="text-center text-muted-foreground max-w-md mx-auto mb-12">
            No apps to download, no DVSA refreshing. We handle everything.
          </p>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              {
                step: "1",
                title: "Sign Up",
                desc: "Takes 2 minutes. Pick your test centre and dates.",
                icon: Smartphone,
              },
              {
                step: "2",
                title: "Pay & Chill",
                desc: "One-off payment. We start scanning immediately. No subscription.",
                icon: CreditCard,
              },
              {
                step: "3",
                title: "Get Your Test",
                desc: "We grab the first matching cancellation and book it. You just show up.",
                icon: CheckCircle,
              },
            ].map((s) => (
              <div key={s.step} className="text-center group">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl button-gradient text-primary-foreground text-2xl font-black transition-transform group-hover:scale-110 shadow-lg">
                  {s.step}
                </div>
                <h3 className="text-lg font-bold text-foreground mb-1">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/signup">
              <Button size="lg" className="rounded-full px-8 py-6 text-base font-semibold">
                Let's Go <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-14 bg-muted/30 overflow-hidden">
        <div className="container mx-auto px-4 mb-8 text-center">
          <div className="flex items-center justify-center gap-1 mb-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-6 w-6 fill-yellow-400 text-yellow-400" />
            ))}
          </div>
          <h2 className="text-3xl font-bold text-foreground mb-1">People Love Us</h2>
          <p className="text-muted-foreground">3,000+ verified 5-star reviews</p>
        </div>
        <div className="space-y-4">
          <ReviewRow items={reviews} direction="left" />
          <ReviewRow items={reviews2} direction="right" />
        </div>
      </section>

      {/* Pricing — cleaner, more direct */}
      <section id="pricing" className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-2 text-foreground">Simple Pricing</h2>
          <p className="text-center text-muted-foreground mb-10">
            Pay once. No monthly fees. See pricing after signup.
          </p>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* 1. Standard */}
            <Card className="border border-border card-gradient hover:border-primary/40 transition-colors shadow-sm">
              <CardHeader className="text-center pb-2">
                <p className="text-sm font-medium text-muted-foreground mb-1">Already have a DVSA booking?</p>
                <CardTitle className="text-2xl font-black text-foreground">Standard</CardTitle>
                <p className="text-xl font-bold text-foreground mt-1">£48</p>
                <p className="text-xs text-muted-foreground">one-off payment</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2">
                  {["Cancellation search at your centre", "Auto-booking when found", "Email updates"].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-card-foreground">
                      <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  className="w-full py-6 text-base font-semibold rounded-full"
                  size="lg"
                  style={{ background: "#16a34a", color: "white" }}
                >
                  <Link to="/signup">Get Started</Link>
                </Button>
              </CardContent>
            </Card>

            {/* 2. Priority — Most Popular */}
            <Card className="border-2 border-primary card-gradient relative shadow-xl ring-2 ring-primary/20">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="button-gradient text-primary-foreground text-xs font-bold px-4 py-1 rounded-full shadow-md">
                  Most Popular
                </span>
              </div>
              <CardHeader className="text-center pb-2 pt-8">
                <p className="text-sm font-medium text-muted-foreground mb-1">Already have a DVSA booking?</p>
                <CardTitle className="text-2xl font-black text-foreground">Priority</CardTitle>
                <p className="text-xl font-bold text-foreground mt-1">£98</p>
                <p className="text-xs text-muted-foreground">one-off · fastest results</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2">
                  {[
                    "Everything in Standard",
                    "Queue priority",
                    "High-demand centres included",
                    "Dedicated search agent",
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-card-foreground">
                      <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  className="w-full py-6 text-base font-semibold rounded-full"
                  size="lg"
                  style={{ background: "#16a34a", color: "white" }}
                >
                  <Link to="/signup">Get Started</Link>
                </Button>
              </CardContent>
            </Card>

            {/* 3. Full Support */}
            <Card className="border border-border card-gradient hover:border-primary/40 transition-colors shadow-sm">
              <CardHeader className="text-center pb-2">
                <p className="text-sm font-medium text-muted-foreground mb-1">No booking yet? We'll sort it</p>
                <CardTitle className="text-2xl font-black text-foreground">Full Support</CardTitle>
                <p className="text-xl font-bold text-foreground mt-1">£148</p>
                <p className="text-xs text-muted-foreground">one-off · DVSA fee included</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2">
                  {[
                    "Everything in Standard",
                    "We book your first DVSA test",
                    "Then search for earlier dates",
                    "Priority dashboard support",
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-card-foreground">
                      <CheckCircle className="h-4 w-4 text-accent flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  className="w-full py-6 text-base font-semibold rounded-full"
                  size="lg"
                  style={{ background: "#16a34a", color: "white" }}
                >
                  <Link to="/signup">Get Started</Link>
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Trust bar — guarantees */}
          <div className="mt-12 max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: CheckCircle, title: "DVSA-Safe", text: "Fully compliant process" },
                { icon: Clock, title: "24/7 Searching", text: "Never sleeps, never stops" },
                { icon: CreditCard, title: "Secure Payment", text: "Encrypted via Stripe" },
                { icon: Handshake, title: "No Slot, No Fee*", text: "Fair refund policy" },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex flex-col items-center text-center gap-2 p-4 rounded-xl border border-border bg-card/50 hover:border-primary/40 hover:shadow-md transition-all"
                >
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <p className="font-semibold text-sm text-foreground">{item.title}</p>
                  <p className="text-xs text-muted-foreground">{item.text}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-muted-foreground mt-4">
              *Refund eligibility subject to our terms. Trusted by 12,000+ UK learners.
            </p>
          </div>
        </div>
      </section>

      {/* Why Us — quick scannable section */}
      <section className="py-16 hero-gradient relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            background: "radial-gradient(circle at 70% 50%, hsl(var(--primary-glow)) 0%, transparent 50%)",
          }}
        />
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-3xl font-bold text-center mb-10 text-primary-foreground">Why Everyone Uses Us</h2>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
            {[
              {
                icon: GraduationCap,
                title: "Works Around You",
                desc: "Set blackout dates for exams, work, or holidays. We won't book anything that clashes.",
              },
              {
                icon: Zap,
                title: "Faster Than You",
                desc: "Our system grabs cancellations in milliseconds. You literally can't do it faster manually.",
              },
              {
                icon: Handshake,
                title: "Transparent",
                desc: "Clear pricing, no hidden fees, no credit systems, no tricks.",
              },
            ].map((f) => (
              <div key={f.title} className="text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/20">
                  <f.icon className="h-6 w-6 text-accent" />
                </div>
                <h3 className="font-bold text-primary-foreground mb-1">{f.title}</h3>
                <p className="text-sm text-primary-foreground/80">{f.desc}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto text-center">
            {[
              { label: "Monitoring", value: "24/7" },
              { label: "Preference Changes", value: "Unlimited" },
              { label: "Notifications", value: "Instant" },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-2xl md:text-3xl font-black text-primary-foreground">{s.value}</p>
                <p className="text-xs text-primary-foreground/80 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 bg-background">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-2 text-foreground">Got Questions?</h2>
          <p className="text-center text-muted-foreground mb-10">Here's everything you need to know.</p>

          <div className="space-y-3">
            {faqs.map((faq) => (
              <FAQItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA — urgent, action-oriented */}
      <section className="py-14 bg-primary">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-black mb-3 text-primary-foreground">Your Test Is Waiting 🚗</h2>
          <p className="text-primary-foreground mb-6 max-w-md mx-auto">
            2-minute signup. We start searching instantly. You could have a new date by tomorrow.
          </p>
          <Link to="/signup">
            <Button size="lg" variant="secondary" className="px-10 py-6 text-base font-bold rounded-full">
              Sign Up Now — It's Quick <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-6 bg-card">
        <div className="container mx-auto px-4 text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Cancellations Express. All rights reserved.
          </p>
          <p className="text-[11px] text-muted-foreground">
            Independent service. Not affiliated with the DVSA or the UK Government.
          </p>
          <nav className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <Link to="/terms" className="hover:text-foreground">
              Terms of Service
            </Link>
            <Link to="/refund-policy" className="hover:text-foreground">
              Refund Policy
            </Link>
            <Link to="/privacy" className="hover:text-foreground">
              Privacy Policy
            </Link>
            <Link to="/cookies" className="hover:text-foreground">
              Cookie Policy
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default Index;
